import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import AutocompleteInput from '../../autocomplete-input';
import {
  useCreateUserMutation,
  useUploadFileMutation,
} from '../../../services/api';
import { useNotificationsProvider } from '../../../context/NotificationsContext';
import { ReactComponent as DefaultUserIcon } from '../../../assets/ic_default_user.svg';
import { UserIcon, State } from '../../UserIcon';
import validators from '../../../utils/validators';

AddUserDialog.propTypes = {
  title: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onCreated: PropTypes.func,
};

export default function AddUserDialog({ title, open, onClose, onCreated }) {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();

  const fileInputRef = useRef(null);

  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [uploadFile] = useUploadFileMutation();

  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoState, setPhotoState] = useState(null);

  const schema = z.object({
    firstName: z.string().trim().min(1, t('user.errors.firstName-required', 'First name is required')),
    lastName: z.string().trim().min(1, t('user.errors.lastName-required', 'Last name is required')),
    email: z
      .string()
      .trim()
      .min(1, t('user.errors.email-required', 'Email is required'))
      .refine(
        (v) => validators.isValidEmail(v),
        { message: t('user.errors.email-invalid', 'Invalid email') },
      ),
    role: z
      .any()
      .refine((v) => v && (typeof v === 'string' ? v.length > 0 : !!v.id), {
        message: t('user.errors.role-required', 'Role is required'),
      }),
  });

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: '',
    },
  });

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const resetState = () => {
    form.reset({ firstName: '', lastName: '', email: '', role: '' });
    setPhoto(null);
    setPhotoUrl(null);
    setPhotoState(null);
  };

  const onOpenChange = (next) => {
    if (!next) {
      if (isCreating) return;
      resetState();
      onClose();
    }
  };

  const onSubmit = async (values) => {
    const data = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      role: values.role.id,
    };
    if (photoUrl) data.photoUrl = photoUrl;

    try {
      await createUser(data).unwrap();
      showNotification(
        'success',
        t('user.add-user-dialog.success', {
          name: `${values.firstName} ${values.lastName}`,
        }),
      );
      resetState();
      onCreated();
    } catch {
      showNotification(
        'error',
        t('user.add-user-dialog.error', {
          name: `${values.firstName} ${values.lastName}`,
        }),
      );
    }
  };

  const handleImageUpload = async (event) => {
    const image = event.target.files[0];
    if (!image) return;
    setPhotoState(State.Loading);
    setPhoto(URL.createObjectURL(image));
    try {
      const result = await uploadFile({
        file: image,
        meta: { contentType: image.type },
      }).unwrap();
      setPhotoUrl(result.url);
      setPhotoState(State.Success);
    } catch {
      setPhotoState(State.Error);
      showNotification('error', t('common.api-error'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
        onInteractOutside={(e) => { if (isCreating) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (isCreating) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{title ?? t('user.add-user-dialog.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {title ?? t('user.add-user-dialog.title')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-5"
            noValidate
          >
            {/* Avatar uploader: hidden file input + clickable thumbnail. */}
            <div className="flex flex-col items-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
              <button
                type="button"
                onClick={handleAvatarClick}
                className="rounded-full p-1 transition hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {photo ? (
                  <UserIcon src={photo} width={58} height={58} state={photoState} />
                ) : (
                  <DefaultUserIcon />
                )}
              </button>
              <button
                type="button"
                onClick={handleAvatarClick}
                className="mt-1 cursor-pointer text-xs text-muted-foreground hover:underline"
              >
                {t('user.add-user-dialog.upload-photo')}
              </button>
            </div>

            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="after:ml-0.5 after:text-destructive after:content-['*']">
                    {t('user.add-user-dialog.firstName-title')}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="after:ml-0.5 after:text-destructive after:content-['*']">
                    {t('user.add-user-dialog.lastName-title')}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="after:ml-0.5 after:text-destructive after:content-['*']">
                    {t('user.add-user-dialog.email-title')}
                  </FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field, fieldState }) => (
                <FormItem>
                  <AutocompleteInput
                    error={!!fieldState.error}
                    onChange={(val) => field.onChange(val)}
                    onInputChange={(val) => field.onChange(val)}
                    label={t('user.add-user-dialog.role-title')}
                    resource="users/roles-autocomplete"
                    required
                    canDelete
                    refreshOnInputChange
                    clearOnBlur={false}
                    freeSolo
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isCreating}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                {t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
